
import { getModule } from "vuex-module-decorators";
import ContentModule from "../../store/modules/ContentModule";
import { CategoryModel } from "@/models/CategoryModel";
import { TopicModel } from "@/models/TopicModel";
import { Helpers } from "@/global-scripts/helpers";
import { Component, Vue, Watch } from "vue-property-decorator";
import SearchModule from "@/store/modules/SearchModule";
import FilterModule from "@/store/modules/FilterModule";
import SearchInput from "@/components/SearchInput/SearchInput.vue";
import CategoryList from "@/components/CategoryList/CategoryList.vue";
import FilterNewsToggle from "@/components/FilterNewsToggle/FilterNewsToggle.vue";
import TopicList from "@/components/TopicList/TopicList.vue";
import $ from "jquery";
/* eslint-disable-next-line */
import KeyDownEvent = JQuery.KeyDownEvent;
/* eslint-disable-next-line */
import KeyUpEvent = JQuery.KeyUpEvent;
import { SearchHelper } from "@/global-scripts/search-helper";
import MemberModule from "@/store/modules/MemberModule";

@Component({
  components: {
    SearchInput,
    CategoryList,
    FilterNewsToggle,
    TopicList,
  },
})
export default class SearchModal extends Vue {
  readonly contentModule = getModule(ContentModule, this.$store);
  readonly searchModule = getModule(SearchModule, this.$store);
  readonly filterModule = getModule(FilterModule, this.$store);
  readonly memberModule = getModule(MemberModule, this.$store);

  processTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  processing = false;
  inputIsFocused = false;
  updateTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  get history(): string[] {
    return this.searchModule.searchHistory;
  }

  get noHits(): boolean {
    return this.filter.length > 0 && this.searchData.length === 0;
  }

  // Local getter & setter for Search filter
  get filter() {
    // Get from the store
    return this.searchModule.searchFilter.length > this.$config.searchMinChars ? this.searchModule.searchFilter : "";
  }

  set filter(str: string) {
    str = str.trim();
    // Dispatch store action
    this.searchModule.setSearchFilter(str);
  }

  get filteredCategories(): CategoryModel[] {
    return this.contentModule.filteredCategories;
  }

  /**
   * Returns the current search data
   */
  get searchData(): TopicModel[] {
    if (this.filter.length === 0) {
      return [];
    }

    return SearchHelper.filterTopics(this.contentModule.filteredTopics, this.filter);
  }

  get searchModalOpen(): boolean {
    return this.searchModule.searchModalOpen;
  }

  onTopicClick(topic: TopicModel) {
    this.closeSearchModal();
    this.$emit("click");
    
    if (this.filter && topic) {
      // Save filter to history
      this.addKeyword(this.filter);
      this.$tracker.trackSearchHitClick(
        this.filter,
        this.searchData.length,
        topic,
        this.filterModule.basePath,
        this.contentModule.locale
      );
    }
  }

  onCategoryClick(category: CategoryModel) {
    // Scroll to top if selected same page
    if (this.$route.name === "Category") {
      const topCategoryId = this.contentModule.activeTopCategory ? this.contentModule.activeTopCategory.id : -1;

      if (category.id === topCategoryId) {
        Helpers.scrollTo("body");
      }
    }

    this.closeSearchModal();
    this.$emit("click");
  }

  setKeyword(keyword: string) {
    this.$emit("setFilterValue", keyword);
    this.$tracker.trackSearchEvent("keyword history", keyword);

    setTimeout(() => {
      // Attempt to remove keyword from history if it returned an empty result
      if (this.filter && this.searchData.length === 0) {
        this.removeKeyword(this.filter);
      }
    }, 1000);
  }

  addKeyword(keyword: string) {
    this.searchModule.addSearchKeyword(keyword);
  }

  removeKeyword(keyword: string) {
    this.searchModule.removeSearchKeyword(keyword);
  }

  // Create fake processing time
  process() {
    clearTimeout(this.processTimer);
    this.processing = true;
    this.processTimer = setTimeout(() => {
      this.processing = false;
    }, this.$config.processTime);
  }

  // Trigger first item in search list when enter is pressed!
  onEnterKey() {
    if ($(".SearchResults .topic-nav").length) {
      $(".SearchResults .topic-nav:first-child").trigger("click");
    }
  }

  initKeyEvents() {
    const $body = $("body");
    const validKeys = "abcdefghijklmnopqrstuvwxyzåäö0123456789";

    $(window)
      .on("focus", function () {
        $body.addClass("is-focused");
      })
      .on("blur", function () {
        $body.removeClass("is-focused");
      });

    $body
      .on("keydown", (e: KeyDownEvent) => {
        const $input = $("#modal-search-input");
        const modKey = e.metaKey || e.altKey || e.ctrlKey;
        let key = e.key; //String.fromCharCode(e.charCode);
        key = key ? key.toLowerCase() : "";

        if (this.contentModule.feedbackFormOpen) return; // Feedback form should have the focus!

        if (
          !modKey &&
          key &&
          validKeys.indexOf(key) !== -1 &&
          !this.searchModalOpen &&
          !this.inputIsFocused &&
          $input.length
        ) {
          this.filter = ""; // Clear it for next filter
          this.openSearchModal();
          this.$emit("focusInput");
        }
      })
      .on("keyup", (e: KeyUpEvent) => {
        // ESCAPE KEY (can only be detected in keyup event)
        if (e.keyCode === 27 && !this.inputIsFocused) {
          // If input isn't focused => close modal
          this.closeSearchModal();
        }
      });
  }

  destroyKeyEvents() {
    $(window).off("focus").off("blur");

    $("body").off("keydown").off("keyup");
  }

  scrollToTop() {
    setTimeout(() => {
      $(".SearchModal").stop().animate(
        {
          scrollTop: 0,
        },
        250
      );
    }, 100);
  }

  openSearchModal() {
    this.searchModule.setSearchModalOpen(true);
  }

  closeSearchModal() {
    this.searchModule.setSearchModalOpen(false);
  }

  updateSearchModalPos() {
    const $modal = $(".SearchModal");
    const $win = $(window);
    const $subMenu = $("#submenu");

    const winWidth = $win.width() ? ($win.width() as number) : 0;
    const offset = $subMenu.offset();
    const offsetTop = offset ? offset.top : 0;
    const height = $subMenu.height() ? ($subMenu.height() as number) : 0;
    const scrollTop = $win.scrollTop() ? ($win.scrollTop() as number) : 0;

    if ($modal.length && winWidth > 768) {
      $modal.css("top", offsetTop + height - scrollTop);
    } else if ($modal.length) {
      // Small screen
      $modal.css("top", 0);
    }
  }

  @Watch("searchModalOpen")
  onSearchModalOpenChange(newVal: boolean, oldVal: boolean) {
    const $body = $("body");
    const $topNav = $(".top-nav");

    const modalOpened = !oldVal && newVal;
    const modalClosed = oldVal && !newVal;

    // Modal has been opened => focus input
    if (modalOpened) {
      this.updateSearchModalPos();
      this.$emit("focusInput");

      // Attach close event when clicked anywhere on top navigation
      $topNav.on("click", () => {
        this.closeSearchModal();
        $topNav.off("click");
      });

      Helpers.setScrollBarOffset();

      setTimeout(() => {
        this.updateSearchModalPos();
      }, 100);
      $(document).trigger("SearchModal:open");
    } else if (modalClosed) {
      // Detach click event on top-nav
      $topNav.off("click");

      Helpers.removeScrollBarOffset();
      $(document).trigger("SearchModal:close");
    }

    // Update Route
    const href = window.location.href;
    const hash = this.$route.hash;

    clearTimeout(this.updateTimer);

    if (modalOpened) {
      // Sets a delay to reduce the flash when updating route
      this.updateTimer = setTimeout(() => {
        history.pushState(null, "", `${this.$route.path}?${this.$config.searchParamName}=1${hash}`);
      }, 250);
    } else {
      history.pushState(null, "", `${this.$route.path}${hash}`);
    }
  }

  destroyed() {
    this.destroyKeyEvents();
  }

  created() {
    this.initKeyEvents();

    this.$nextTick(() => {
      this.updateSearchModalPos();

      $(window).resize(this.updateSearchModalPos);
    });
  }
}
